import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined"
import CallOutlinedIcon from "@mui/icons-material/CallOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined"
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined"
import PhoneForwardedOutlinedIcon from "@mui/icons-material/PhoneForwardedOutlined"
import SendOutlinedIcon from "@mui/icons-material/SendOutlined"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Grid from "@mui/material/Grid"
import Link from "@mui/material/Link"
import TableCell from "@mui/material/TableCell"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"
import { useEffect, useState } from "react"
import { Link as RouterLink, useNavigate } from "react-router-dom"

import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row from "../../../common/components/Row"
import { buildActivityReportEmailsLink } from "../activity-report-logs/reportDateRangePresets"
import { adminLiquidEmptyStateSx } from "../adminStyles"
import { adminApi } from "../axios"
import AdminDashboardPanel from "../components/AdminDashboardPanel"
import AdminLiquidSurface from "../components/AdminLiquidSurface"
import AdminPageHeader from "../components/AdminPageHeader"
import StatCard from "../components/StatCard"

function formatEventType(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function eventTypeChipColor(eventType) {
  if (String(eventType).includes("denied")) return "error"
  if (String(eventType).includes("assignment")) return "primary"
  if (String(eventType).includes("calling")) return "secondary"
  return "default"
}

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const reports30d = data?.activity_reports_30d
  const activityReportsLink = buildActivityReportEmailsLink({
    rangePreset: "last_30_days",
  })

  useEffect(() => {
    adminApi
      .fetchDashboard()
      .then(({ data: dashboard }) => setData(dashboard))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Column gap={3}>
      <ConditionalPurpleLinearProgress visible={loading} />
      <AdminPageHeader
        title="Dashboard"
        subtitle="Operational overview for concierge staffing, assignments, and recent activity."
      />

      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Active concierges"
            value={data?.active_concierges ?? 0}
            icon={GroupsOutlinedIcon}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Active assignments"
            value={data?.active_assignments ?? 0}
            icon={AssignmentIndOutlinedIcon}
            accent="success"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Twilio numbers"
            value={data?.calling_numbers ?? 0}
            icon={PhoneForwardedOutlinedIcon}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Connected mailboxes"
            value={data?.connected_mailboxes ?? 0}
            icon={MailOutlineOutlinedIcon}
            accent="neutral"
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            label="Reports sent (30d)"
            value={reports30d?.reports_sent ?? 0}
            icon={SendOutlinedIcon}
            accent="success"
            loading={loading}
            onClick={() => navigate(activityReportsLink)}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            label="Reports pending (30d)"
            value={reports30d?.reports_pending ?? 0}
            icon={PendingActionsOutlinedIcon}
            loading={loading}
            onClick={() => navigate(activityReportsLink)}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            label="Reports failed (30d)"
            value={reports30d?.reports_failed ?? 0}
            icon={ErrorOutlineOutlinedIcon}
            accent="neutral"
            loading={loading}
            onClick={() => navigate(activityReportsLink)}
          />
        </Grid>
      </Grid>

      <AdminLiquidSurface variant="card" sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Column gap={2}>
          <Column gap={0.25}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "colors.gray800" }}
            >
              Activity (last 30 days)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Outreach volume logged by concierge users across channels.
            </Typography>
          </Column>
          <Grid container spacing={{ xs: 2, sm: 2.5 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Calls"
                value={data?.activity_30d?.calls ?? 0}
                sublabel="Logged by concierge users"
                icon={CallOutlinedIcon}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="SMS"
                value={data?.activity_30d?.sms ?? 0}
                icon={SmsOutlinedIcon}
                accent="success"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Emails"
                value={data?.activity_30d?.emails ?? 0}
                icon={EmailOutlinedIcon}
                accent="neutral"
                loading={loading}
              />
            </Grid>
          </Grid>
        </Column>
      </AdminLiquidSurface>

      <Grid container spacing={{ xs: 2, sm: 2.5 }} alignItems="stretch">
        <Grid item xs={12} lg={6}>
          <AdminDashboardPanel
            title="Top active concierges"
            subtitle="Ranked by audit events in the last 30 days."
            viewAllTo="/admin/concierges"
            columns={[
              { id: "concierge", label: "Concierge" },
              { id: "events", label: "Events (30d)", align: "right" },
            ]}
            rows={data?.top_concierges || []}
            emptyMessage="No concierge activity recorded in the last 30 days."
            renderRow={(row) => (
              <TableRow key={row.concierge_user_id} hover>
                <TableCell sx={{ borderBottomColor: "colors.gray200" }}>
                  <Link
                    component={RouterLink}
                    to={`/admin/concierges/${row.concierge_user_id}`}
                    underline="hover"
                    sx={{ fontWeight: 600 }}
                  >
                    {row.name || row.email}
                  </Link>
                  {row.email && row.name ? (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      {row.email}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ borderBottomColor: "colors.gray200", fontWeight: 600 }}
                >
                  {row.activity_count?.toLocaleString?.() ?? row.activity_count}
                </TableCell>
              </TableRow>
            )}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <AdminDashboardPanel
            title="Recent concierge logs"
            subtitle="Latest security and operational events."
            viewAllTo="/admin/audit-logs"
            columns={[
              { id: "event", label: "Event" },
              { id: "concierge", label: "Concierge" },
              { id: "when", label: "When" },
            ]}
            rows={data?.recent_logs || []}
            emptyMessage="No audit events have been recorded yet."
            renderRow={(log) => (
              <TableRow key={log.id} hover>
                <TableCell sx={{ borderBottomColor: "colors.gray200" }}>
                  <Chip
                    size="small"
                    label={formatEventType(log.event_type)}
                    color={eventTypeChipColor(log.event_type)}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell sx={{ borderBottomColor: "colors.gray200" }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {log.concierge_name || log.concierge_email || "—"}
                  </Typography>
                  {log.account_owner_name || log.account_owner_email ? (
                    <Typography variant="caption" color="text.secondary">
                      {log.account_owner_name || log.account_owner_email}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell
                  sx={{
                    borderBottomColor: "colors.gray200",
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  {log.created
                    ? format(new Date(log.created), "MMM d, h:mm a")
                    : "—"}
                </TableCell>
              </TableRow>
            )}
          />
        </Grid>
      </Grid>

      {!loading && data ? (
        <Row sx={{ justifyContent: "flex-end" }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              borderRadius: "999px",
              ...adminLiquidEmptyStateSx,
              borderStyle: "solid",
            }}
          >
            <Typography
              variant="caption"
              color="colors.brand700"
              sx={{ fontWeight: 600 }}
            >
              {data.active_concierges} active concierges ·{" "}
              {data.active_assignments} open assignments
            </Typography>
          </Box>
        </Row>
      ) : null}
    </Column>
  )
}
